import type { ScanReport } from "./types";

function csvCell(value: unknown) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function reportToMarkdown(report: ScanReport) {
  const lines = [
    `# JarLab report: ${report.fileName}`,
    "",
    `- Verdict: **${report.verdict}**`,
    `- Score: **${report.score}/100**`,
    `- SHA-256: \`${report.hashes.sha256}\``,
    `- Analyzer: ${report.analyzerVersion}`,
    `- Completed: ${report.completedAt}`,
    `- Behavior chains: ${report.behaviorChains.length}`,
    `- Known-hash matches: ${report.knownHashMatches.length}`,
    `- Repack matches: ${report.fingerprintMatches.length}`,
    `- YARA rules loaded: ${report.yaraRulesLoaded}`,
    ...(report.advisoryDataset
      ? [
          `- Vulnerability dataset: ${report.advisoryDataset.source}, cutoff ${report.advisoryDataset.fetchedAt}`,
          `- Dataset coverage: ${report.advisoryDataset.advisoryCount.toLocaleString()} advisories across ${report.advisoryDataset.artifactCount.toLocaleString()} Maven artifacts`,
          `- A dependency absent from this dataset is not known vulnerable as of the cutoff date, which is not the same as not being vulnerable.`,
        ]
      : []),
    `- YARA rule matches: ${report.yaraMatches.length}`,
    ...(report.yaraUnsupported.length
      ? [
          `- **YARA rules that did not run: ${report.yaraUnsupported.length}**`,
        ]
      : []),
    ...(report.advisoryDataset
      ? []
      : [
          `- **No vulnerability dataset: ${
              report.advisoryDatasetError
                ? `it could not be loaded (${report.advisoryDatasetError})`
                : "no Maven coordinates were resolved from this sample"
            }.**`,
        ]),
    "",
    // First section on purpose. Everything below it is scoped by what did not
    // run, and a report read cold has no other way to learn that.
    ...(report.warnings.length
      ? [
          "## Coverage caveats",
          "",
          `${report.warnings.length} thing(s) below did not run, or ran without full coverage. This report is a statement about what was examined, not a clean bill of health.`,
          "",
          ...report.warnings.map((warning) => `- ${warning}`),
          "",
        ]
      : []),
    ...(report.fingerprintMatches.length
      ? [
          "## Repack matches",
          "",
          ...report.fingerprintMatches.flatMap((match) => [
            `### ${match.family} · ${match.variant}`,
            "",
            `**${match.similarity.toFixed(1)}%** structural similarity (pack threshold ${match.threshold.toFixed(1)}%)`,
            "",
            `Reference simhash: \`${match.simhash64}\``,
            "",
            `Source: ${match.source} · trust: ${match.trust ?? "built-in"}`,
            "",
          ]),
        ]
      : []),
    // Unsupported rules come before the findings on purpose. An exported report
    // is read long after the scan, and "these rules did not run" is the part
    // that changes how the rest of it should be read.
    ...(report.yaraUnsupported.length
      ? [
          "## YARA rules that did not run",
          "",
          "These rules were loaded but could not be evaluated, so anything they",
          "would have matched is **not covered** by this report.",
          "",
          ...report.yaraUnsupported.flatMap((item) => [
            `- **${item.rule}** — unsupported construct \`${item.construct}\` (line ${item.line})`,
            `  - ${item.detail}`,
          ]),
          "",
        ]
      : []),
    ...(report.dependencyAdvisories?.length
      ? [
          "## Known-vulnerable dependencies",
          "",
          report.advisoryDataset
            ? `Dataset: ${report.advisoryDataset.source}, cutoff ${report.advisoryDataset.fetchedAt}. A dependency not listed here is not known vulnerable as of that date, which is not the same as not being vulnerable.`
            : "",
          "",
          ...report.dependencyAdvisories!.flatMap((entry) => [
            `### ${entry.coordinate}`,
            "",
            `${entry.advisoryId}${entry.aliases.length ? ` (${entry.aliases.join(", ")})` : ""}`,
            "",
            `- CVSS: ${entry.cvssScore === null ? "not published upstream" : `${entry.cvssScore} (${entry.cvssVector ?? "vector unavailable"})`}`,
            `- Fixed in: ${entry.fixedVersions.join(", ") || "no fixed version published"}`,
            `- Matched by: ${entry.tier === "exact" ? "exact coordinate" : "OSV affected range"}`,
            `- Coordinate: ${entry.coordinateWasRead ? "read from the coordinate file" : "inferred from the entry path"}`,
            "",
            entry.summary,
            "",
          ]),
        ]
      : []),
    ...(report.advisoryUnchecked.length
      ? [
          "## Dependencies not checked",
          "",
          "These coordinates could not be fully resolved, so no advisory lookup was performed. They are not known to be clean.",
          "",
          ...(report.advisoryDataset
            ? [
                `Dataset: ${report.advisoryDataset.source}, cutoff ${report.advisoryDataset.fetchedAt}.`,
                "",
              ]
            : []),
          "",
          ...report.advisoryUnchecked.map((entry) => `- \`${entry.source}\` — ${entry.reason}`),
          "",
        ]
      : []),
    ...(report.yaraMatches.length
      ? [
          "## YARA rule matches",
          "",
          ...report.yaraMatches.flatMap((match) => [
            `### ${match.rule} (\`${match.ruleId}\`)`,
            "",
            `${match.severity.toUpperCase()} · ${match.confidence} confidence · ${match.target} (${match.surface})`,
            "",
            ...match.evidence.map((item) => `- \`${item}\``),
            "",
          ]),
        ]
      : []),
    "## Findings",
    "",
    ...report.findings.flatMap((finding) => [
      `### ${finding.title}`,
      "",
      `**${finding.severity.toUpperCase()}** · ${finding.category}`,
      "",
      finding.description,
      "",
      ...finding.evidence.map((item) => `- \`${item}\``),
      "",
    ]),
    "## Behavior chains",
    "",
    ...report.behaviorChains.flatMap((chain) => [
      `### ${chain.title}`,
      "",
      `${chain.severity.toUpperCase()} · ${chain.confidence} confidence · ${chain.ordering}`,
      "",
      chain.description,
      "",
      ...chain.stages.map(
        (stage) =>
          `${stage.index + 1}. **${stage.title}**: ${stage.detail} (\`${stage.entry}\`)`,
      ),
      "",
    ]),
    "## Observed actions",
    "",
    ...report.actions.map(
      (action) =>
        `- **${action.title}** (${action.severity}, ${action.confidence}): ${action.description}`,
    ),
    "",
    "## Indicators",
    "",
    ...report.indicators.map(
      (indicator) =>
        `- **${indicator.type}**: \`${indicator.value}\` (${indicator.risk})`,
    ),
    "",
    "> Static analysis cannot guarantee that a file is safe.",
    "",
  ];
  return lines.join("\n");
}

export function reportToCsv(report: ScanReport) {
  const rows: unknown[][] = [
    ["record_type", "severity", "title_or_type", "description_or_value", "entry", "confidence"],
    ...report.findings.map((item) => [
      "finding",
      item.severity,
      item.title,
      item.description,
      item.entry ?? "",
      item.confidence ?? "",
    ]),
    ...report.actions.map((item) => [
      "action",
      item.severity,
      item.title,
      item.description,
      item.entry,
      item.confidence,
    ]),
    ...report.indicators.map((item) => [
      "indicator",
      item.risk,
      item.type,
      item.value,
      item.entry,
      "",
    ]),
    // Exported so an advisory is auditable in the spreadsheet, including the
    // "no CVSS published" case, which is information rather than an omission.
    ...report.dependencyAdvisories.map((item) => [
      "dependency_advisory",
      item.severity,
      item.advisoryId,
      item.summary,
      `${item.coordinate} (${item.tier === "exact" ? "exact coordinate" : "OSV range"})`,
      item.cvssScore === null ? "not published" : String(item.cvssScore),
    ]),
    ...report.advisoryUnchecked.map((item) => [
      "dependency_unchecked",
      "",
      "",
      item.reason,
      item.source,
      "",
    ]),
    ...report.yaraMatches.map((item) => [
      "yara_match",
      item.severity,
      item.ruleId,
      item.evidence.join(" | "),
      `${item.target} (${item.surface})`,
      item.confidence,
    ]),
    // Exported so a rule that silently did not run is visible in the
    // spreadsheet too, not only in the Markdown.
    ...report.yaraUnsupported.map((item) => [
      "yara_unsupported",
      "info",
      item.rule,
      `${item.construct} (line ${item.line}): ${item.detail}`,
      "",
      "",
    ]),
    // The cutoff is what makes a negative advisory result mean anything, so it
    // travels with the rows rather than only in the Markdown header.
    ...(report.advisoryDataset
      ? [
          [
            "advisory_dataset",
            "",
            report.advisoryDataset.source,
            `${report.advisoryDataset.advisoryCount} advisories across ${report.advisoryDataset.artifactCount} Maven artifacts; ${report.advisoryDataset.withCvss} with a CVSS score, ${report.advisoryDataset.withoutCvss} without`,
            `cutoff ${report.advisoryDataset.fetchedAt}`,
            "",
          ],
        ]
      : [
          [
            "advisory_dataset",
            "",
            "not available",
            report.advisoryDatasetError
              ? `the vulnerability dataset could not be loaded: ${report.advisoryDatasetError}`
              : "no Maven coordinates were resolved from this sample, so no advisory lookup was performed",
            "",
            "",
          ],
        ]),
    // Every caveat the report carries, in the same words every other surface
    // uses, so a spreadsheet cannot read as cleaner than the Markdown.
    ...report.warnings.map((warning) => [
      "coverage_caveat",
      "info",
      "coverage caveat",
      warning,
      "",
      "",
    ]),
  ];
  return rows.map((row) => row.map(csvCell).join(",")).join("\n");
}

export function reportToSarif(report: ScanReport) {
  const rules = new Map(
    report.findings.map((finding) => [
      finding.ruleId,
      {
        id: finding.ruleId,
        name: finding.title.replaceAll(/[^A-Za-z0-9]+/g, "_"),
        shortDescription: { text: finding.title },
        fullDescription: { text: finding.description },
        properties: { category: finding.category },
      },
    ]),
  );
  return JSON.stringify(
    {
      $schema:
        "https://json.schemastore.org/sarif-2.1.0.json",
      version: "2.1.0",
      runs: [
        {
          tool: {
            driver: {
              name: "JarLab",
              version: report.analyzerVersion,
              informationUri: "https://jarlab-scanner.asdf243.chatgpt.site",
              rules: Array.from(rules.values()),
            },
          },
          artifacts: [
            {
              location: { uri: report.fileName },
              hashes: { "sha-256": report.hashes.sha256 },
            },
          ],
          // SARIF has no place to put "this did not run", which is exactly why
          // a consumer reading only the results would otherwise take a partial
          // scan for a clean one. Execution notifications are the channel the
          // format provides for it.
          invocations: [
            {
              executionSuccessful: true,
              toolExecutionNotifications:
                report.warnings.length > 0
                  ? report.warnings.map((warning) => ({
                      level: "note",
                      message: { text: warning },
                    }))
                  : [
                      {
                        level: "note",
                        message: {
                          text: "No coverage caveats were recorded for this scan. The absence of findings is not a guarantee that the file is safe.",
                        },
                      },
                    ],
            },
          ],
          results: report.findings.map((finding) => ({
            ruleId: finding.ruleId,
            level:
              finding.severity === "critical" || finding.severity === "high"
                ? "error"
                : finding.severity === "medium"
                  ? "warning"
                  : "note",
            message: { text: finding.description },
            locations: finding.entry
              ? [
                  {
                    physicalLocation: {
                      artifactLocation: { uri: finding.entry },
                    },
                  },
                ]
              : [],
            properties: {
              severity: finding.severity,
              confidence: finding.confidence,
              evidence: finding.evidence,
            },
          })),
          properties: {
            yaraRulesLoaded: report.yaraRulesLoaded,
            yaraUnsupported: report.yaraUnsupported.map((item) => ({
              rule: item.rule,
              construct: item.construct,
              line: item.line,
              detail: item.detail,
            })),
            advisoryDataset: report.advisoryDataset
              ? {
                  source: report.advisoryDataset.source,
                  cutoff: report.advisoryDataset.fetchedAt,
                  advisoryCount: report.advisoryDataset.advisoryCount,
                  artifactCount: report.advisoryDataset.artifactCount,
                  withoutCvss: report.advisoryDataset.withoutCvss,
                  unresolvableRecords: report.advisoryDataset.unresolvableRecords,
                }
              : {
                  available: false,
                  reason: report.advisoryDatasetError
                    ? `dataset could not be loaded: ${report.advisoryDatasetError}`
                    : "no Maven coordinates were resolved from this sample",
                },
            coverageCaveats: report.warnings,
          },
        },
      ],
    },
    null,
    2,
  );
}

/**
 * The README that ships inside the reconstructed-source bundle.
 *
 * This was assembled inline in `App.tsx`, which is the only reason it drifted:
 * the Markdown, CSV, SARIF and SBOM artifacts are all built here, each
 * consuming `report.warnings`, and the one artifact that was not built here was
 * the one that stopped carrying the coverage caveats. An analyst unzipping the
 * bundle was told how many classes were decompiled but not that part of the
 * scan had not run.
 *
 * It now consumes `report.warnings` like every sibling, so the next caveat
 * added to a scan reaches it without anyone remembering this file exists.
 */
export function reportBundleReadme(
  report: ScanReport,
  bundle: {
    decompiledClasses: number;
    decompilerFailures: number;
    materializationLimit: number;
  },
) {
  const truncated = report.classes.length > bundle.materializationLimit;
  // The `""` entries here are paragraph separators, not absent values, so this
  // array is joined directly. A trailing `.filter(Boolean)` — the idiom used
  // elsewhere in this file to drop *conditional fragments* — silently deletes
  // every one of them, which ran the `## Coverage caveats` heading into the
  // line above it. Conditional content is spread in as a group instead, so the
  // only thing that can be omitted is a whole block.
  return [
    "# JarLab reconstructed source bundle",
    "",
    `Sample: ${report.fileName}`,
    `SHA-256: ${report.hashes.sha256}`,
    `Analyzer: ${report.analyzerVersion}`,
    "",
    `Decompiled classes: ${bundle.decompiledClasses}`,
    `Decompiler failures: ${bundle.decompilerFailures}`,
    ...(truncated
      ? [
          `Safety limit: only the first ${bundle.materializationLimit.toLocaleString()} of ${report.classes.length.toLocaleString()} parsed classes were materialized.`,
        ]
      : []),
    "",
    // Same single source as the Markdown, CSV and SARIF artifacts, in the same
    // words. A bundle that reads as complete coverage when it is not is the
    // exact failure these caveats exist to prevent.
    ...(report.warnings.length
      ? [
          "## Coverage caveats",
          "",
          `${report.warnings.length} thing(s) below did not run, or ran without full coverage. This source bundle is a statement about what was examined, not a clean bill of health.`,
          "",
          ...report.warnings.map((warning) => `- ${warning}`),
          "",
        ]
      : []),
    "Uniquely mapped encrypted literals are replaced in recovered source and the remaining values are included as static-analysis annotations.",
    "This source bundle is not runnable. Use Deobfuscate & rebuild for a validated bytecode-rewritten JAR.",
    "No code from the sample was executed.",
    "",
  ].join("\n");
}
